import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Container,
  Row,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AdminFootballTeams,
  BracketMatch,
  PartialPair,
  Tournaments,
  TournamentSchedule,
} from "../../api/Tournaments";
import { FootballMember, FootballTeam, Pair, StandingsCriteria, Tournament } from "../../types/Tournaments";
import { Structures } from "../../api/Structure";
import { UserFavorites } from "../../types/Structure";
import FootballRosterEditor from "../../Components/FootballRosterEditor";
import { useToasts } from "../../contexts/toasts";
import { TailSpin } from "react-loader-spinner";
import { Form, Input, InputNumber, Modal, Radio } from "antd";
import { Pairs } from "../../api/Pairs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faFutbol,
  faBasketball,
  faInfoCircle,
  faMinusCircle,
  faPlus,
  faSave,
  faTableTennisPaddleBall,
  faStar as faSolidStar,
} from "@fortawesome/free-solid-svg-icons";
import { faStar } from "@fortawesome/free-regular-svg-icons";
import BracketItem from "../../Components/BracketItem";
import React from "react";

export type TemporaryPairs = {
  id: string;
  player1Name?: string;
  player2Name?: string;
};

export enum TournamentFormula {
  None = "",
  Gironi = "Gironi",
  Direct = "Eliminazione Diretta",
  Americano = "Americano",
}

export const formulas = [
  { label: TournamentFormula.None, value: TournamentFormula.None },
  { label: TournamentFormula.Gironi, value: TournamentFormula.Gironi },
  { label: TournamentFormula.Direct, value: TournamentFormula.Direct },
  { label: TournamentFormula.Americano, value: TournamentFormula.Americano },
];

// ─── helpers ──────────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  border: "1px solid #e8e8e8",
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
};

const sectionTitle = (text: string) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: "#888",
      marginBottom: 12,
      paddingBottom: 6,
      borderBottom: "1px solid #f0f0f0",
    }}
  >
    {text}
  </div>
);

// "2026-08-06T15:30" -> "06/08 · 15:30"
const formatPreviewDateTime = (dt?: string): string => {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} · ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

// ─── component ────────────────────────────────────────────────────────────────

export default function GenerateSchedule() {
  const { id } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();

  const [tournament, setTournament] = useState<Tournament>();
  const [pairs, setPairs] = useState<Pair[]>();
  const [pairsLoading, setPairsLoading] = useState(false);
  const [tabelloneForm] = Form.useForm();
  const [formula, setFormula] = useState<TournamentFormula>(TournamentFormula.None);
  // Gironi: andata singola o andata e ritorno + criterio della classifica.
  const [roundTrip, setRoundTrip] = useState(false);
  const [standingsCriteria, setStandingsCriteria] =
    useState<StandingsCriteria>("difference");
  // Calcio: pareggi nei gironi decisi ai rigori, con i punti in palio.
  const [shootout, setShootout] = useState(false);
  const [shootoutWinPoints, setShootoutWinPoints] = useState(2);
  const [shootoutLosePoints, setShootoutLosePoints] = useState(1);
  // Calcio: finale 3°/4° posto (eliminazione diretta + gold dei gironi).
  const [thirdPlace, setThirdPlace] = useState(false);
  // Calcio: generazione automatica degli orari (torneo in un solo giorno).
  const [singleDay, setSingleDay] = useState(false);
  const [autoTimes, setAutoTimes] = useState(false);
  const [startDateTime, setStartDateTime] = useState("");
  const [matchDuration, setMatchDuration] = useState(30);
  // Durata partite delle fasi finali (solo gironi + fasi finali).
  const [knockoutDuration, setKnockoutDuration] = useState(30);
  const [gapEnabled, setGapEnabled] = useState(false);
  const [gapMinutes, setGapMinutes] = useState(5);
  const [breakEnabled, setBreakEnabled] = useState(false);
  // Pause multiple: ogni voce è { startTime, durationMinutes }.
  const [breaks, setBreaks] = useState<
    { startTime: string; durationMinutes: number }[]
  >([{ startTime: "", durationMinutes: 30 }]);
  const [multiField, setMultiField] = useState(false);
  const [fieldsCount, setFieldsCount] = useState(2);
  const [testeDiSerie, setTesteDiSerie] = useState<TemporaryPairs[]>([]);
  const temporaryPairs = Form.useWatch("temporaryPairs", tabelloneForm);
  const [bracketLoading, setBracketLoading] = useState(false);
  const [saveBracketLoading, setSaveBracketLoading] = useState(false);
  const [brackets, setBrackets] = useState<TournamentSchedule>();

  const [footballTeams, setFootballTeams] = useState<FootballTeam[]>([]);
  const [favorites, setFavorites] = useState<UserFavorites[]>([]);
  // Staged: id di squadre DB rimosse localmente (verranno eliminate al conferma).
  const [deletedTeamIds, setDeletedTeamIds] = useState<string[]>([]);
  const [addTeamOpen, setAddTeamOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");

  // Basket: come il calcio (squadre, punteggio secco) ma con i punti al posto
  // dei gol e senza rigori; `isFootball` = sport a squadre.
  const isBasket = tournament?.sport?.name === "Basketball";
  const isFootball = tournament?.sport?.name === "Football" || isBasket;
  const participantLabel = isFootball ? "squadra" : "coppia";
  const participantLabelPlural = isFootball ? "squadre" : "coppie";

  // Accento tematico: verde "campo" per il calcio, ambra "parquet" per il
  // basket, arancione brand per il padel.
  const sportTheme = isBasket
    ? {
        accent: "#d97706",
        soft: "#fff7ed",
        dark: "#b45309",
        gradient: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
        label: "Basket",
        icon: faBasketball,
      }
    : isFootball
    ? {
        accent: "#1f8b3b",
        soft: "#eef8f1",
        dark: "#176b2e",
        gradient: "linear-gradient(135deg, #1f8b3b 0%, #14602a 100%)",
        label: "Calcio",
        icon: faFutbol,
      }
    : {
        accent: "#f25d0b",
        soft: "#fff8f5",
        dark: "#d94f08",
        gradient: "linear-gradient(135deg, #f25d0b 0%, #d94f08 100%)",
        label: "Padel",
        icon: faTableTennisPaddleBall,
      };
  const accent = sportTheme.accent;
  const accentSoft = sportTheme.soft;
  const accentDark = sportTheme.dark;
  const accentGradient = sportTheme.gradient;

  // Rose modificabili: carico squadre complete + favoriti del circolo (pool utenti).
  const loadFootballTeams = useCallback(() => {
    if (!tournament) return;
    AdminFootballTeams.getTeams(tournament.id!).then(
      ({ data }) => setFootballTeams(data.teams || []),
      () => setFootballTeams([])
    );
  }, [tournament]);

  useEffect(() => {
    if (tournament && isFootball) {
      loadFootballTeams();
      Structures.getHostFavorites().then(
        ({ data }) => setFavorites(data.list),
        () => setFavorites([])
      );
    }
  }, [tournament, isFootball, loadFootballTeams]);

  // Nell'editor rose mostro il nome digitato live nella riga di seeding
  // (abbinato per id), non quello salvato a DB.
  const teamsWithLiveNames = useMemo(
    () =>
      footballTeams.map((team) => {
        const seed = (temporaryPairs || []).find(
          (p: TemporaryPairs) => p.id === team.id
        );
        return seed?.player1Name ? { ...team, name: seed.player1Name } : team;
      }),
    [footballTeams, temporaryPairs]
  );

  // ─── data loading ─────────────────────────────────────────────────────────

  const retrieveTournament = useCallback(() => {
    Tournaments.getTournament(id!).then(
      ({ data }) => setTournament(data.tournament),
      () => showToast("facility-error", "Errore nel reperimento del torneo", "", "danger")
    );
  }, [showToast, id]);

  useEffect(() => {
    if (id) retrieveTournament();
  }, [id, retrieveTournament]);

  // Orario di inizio di default = data del torneo (troncata a minuti).
  useEffect(() => {
    if (tournament?.date && !startDateTime) {
      setStartDateTime(tournament.date.slice(0, 16));
    }
  }, [tournament, startDateTime]);

  const retrieveParticipants = useCallback(() => {
    if (!tournament) return;
    setPairsLoading(true);

    if (isFootball) {
      AdminFootballTeams.getTeams(tournament.id!).then(
        ({ data }) => {
          setPairsLoading(false);
          tabelloneForm.setFieldValue(
            "temporaryPairs",
            data.teams.map((team, index) => ({
              id: team.id,
              player1Name: team.name,
              player2Name: "",
            }))
          );
        },
        () => {
          setPairsLoading(false);
          showToast("teams-error", "Errore nel reperimento delle squadre", "", "danger");
        }
      );
    } else {
      Pairs.getTournamentPairs(id!).then(
        ({ data }) => {
          setPairsLoading(false);
          setPairs(data.list);
          tabelloneForm.setFieldValue(
            "temporaryPairs",
            // L'id deve essere quello vero della coppia, non l'indice: finisce
            // copiato nel tabellone ed è l'unico aggancio per riallineare i
            // nomi quando poi cambiano i giocatori (vedi "Aggiorna Tabellone").
            // Un posto può essere di un utente registrato o di un ospite: in
            // quel caso il nome sta sulla coppia stessa (`playerXName`).
            data.list.map((pair) => ({
              id: pair.id,
              player1Name: pair.player1
                ? `${pair.player1.name} ${pair.player1.familyName}`
                : (pair.player1Name || "").trim(),
              player2Name: pair.player2
                ? `${pair.player2.name} ${pair.player2.familyName}`
                : (pair.player2Name || "").trim(),
            }))
          );
        },
        () => {
          setPairsLoading(false);
          showToast("pairs-error", "Errore nel reperimento delle coppie", "", "danger");
          setPairs([]);
        }
      );
    }
  }, [showToast, id, tabelloneForm, tournament, isFootball]);

  useEffect(() => {
    if (formula !== TournamentFormula.None && tournament && !temporaryPairs) {
      retrieveParticipants();
    }
  }, [formula, tournament, retrieveParticipants, temporaryPairs]);

  // ─── handlers ─────────────────────────────────────────────────────────────

  const handleFormulaChange = useCallback((value: TournamentFormula) => {
    setFormula(value);
    // Americano: default classifica per scontro diretto (scelta modificabile).
    if (value === TournamentFormula.Americano) setStandingsCriteria("headToHead");
    else if (value === TournamentFormula.Gironi) setStandingsCriteria("difference");
  }, []);

  const handleAddTestaDiSerie = useCallback(
    (index: number) => () => {
      const pair = tabelloneForm.getFieldValue("temporaryPairs")[index];
      setTesteDiSerie((prev) => {
        const exists = prev.find((t) => t.id === pair.id);
        return exists ? prev.filter((t) => t.id !== pair.id) : [...prev, pair];
      });
    },
    [tabelloneForm]
  );

  // Rimuove una squadra (calcio) da rose + seeding + teste di serie. Se e' una
  // squadra gia' salvata a DB la traccio per eliminarla al conferma.
  const removeTeam = useCallback(
    (teamId: string) => {
      setFootballTeams((prev) => prev.filter((t) => t.id !== teamId));
      const all = tabelloneForm.getFieldValue("temporaryPairs") || [];
      tabelloneForm.setFieldValue(
        "temporaryPairs",
        all.filter((p: TemporaryPairs) => p.id !== teamId)
      );
      setTesteDiSerie((prev) => prev.filter((p) => p.id !== teamId));
      if (!teamId.startsWith("local-")) {
        setDeletedTeamIds((prev) =>
          prev.includes(teamId) ? prev : [...prev, teamId]
        );
      }
    },
    [tabelloneForm]
  );

  // Staged: aggiorna la rosa in locale (nessuna chiamata API finche' non confermo).
  const handleMembersChange = useCallback(
    (teamId: string, members: FootballMember[]) => {
      setFootballTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, members } : t))
      );
    },
    []
  );

  // Staged: rinomina la squadra (aggiorna rose + riga di seeding, in sync).
  const handleNameChange = useCallback(
    (teamId: string, name: string) => {
      setFootballTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, name } : t))
      );
      const all = tabelloneForm.getFieldValue("temporaryPairs") || [];
      tabelloneForm.setFieldValue(
        "temporaryPairs",
        all.map((p: TemporaryPairs) =>
          p.id === teamId ? { ...p, player1Name: name } : p
        )
      );
    },
    [tabelloneForm]
  );

  const openAddTeam = useCallback(() => {
    setNewTeamName("");
    setAddTeamOpen(true);
  }, []);

  // Conferma la creazione LOCALE della squadra (verra' salvata al conferma tabellone).
  const confirmAddTeam = useCallback(() => {
    const name = newTeamName.trim();
    if (!name || !tournament) return;
    const id = `local-${Date.now()}`;
    setFootballTeams((prev) => [
      ...prev,
      { id, name, tournamentId: tournament.id!, presidentId: "", members: [] },
    ]);
    const current = tabelloneForm.getFieldValue("temporaryPairs") || [];
    tabelloneForm.setFieldValue("temporaryPairs", [
      ...current,
      { id, player1Name: name, player2Name: "" },
    ]);
    setAddTeamOpen(false);
    setNewTeamName("");
  }, [newTeamName, tournament, tabelloneForm]);

  const handleDelete = useCallback(
    (index: number) => () => {
      const selected = tabelloneForm.getFieldValue("temporaryPairs")[index];
      if (isFootball) {
        removeTeam(selected.id);
        return;
      }
      const all = tabelloneForm.getFieldValue("temporaryPairs");
      tabelloneForm.setFieldValue(
        "temporaryPairs",
        all.filter((p: TemporaryPairs) => p.id !== selected.id)
      );
      setTesteDiSerie((prev) => prev.filter((p) => p.id !== selected.id));
    },
    [tabelloneForm, isFootball, removeTeam]
  );

  const handleAddParticipant = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.preventDefault();
      // Calcio: apro il modale per inserire il nome; la squadra viene aggiunta in
      // locale e salvata solo al conferma del tabellone.
      if (isFootball) {
        openAddTeam();
        return;
      }
      const current = tabelloneForm.getFieldValue("temporaryPairs") || [];
      tabelloneForm.setFieldValue("temporaryPairs", [
        ...current,
        { id: `new-${Date.now()}`, player1Name: "", player2Name: "" },
      ]);
    },
    [tabelloneForm, isFootball, openAddTeam]
  );

  const handleSubmit = useCallback(
    (values: any) => {
      // La formula NON e' un campo del form (e' in state): la prendo da li',
      // altrimenti il payload arriva senza formula e il BE genera direct elimination.
      const isGironi = formula === TournamentFormula.Gironi;
      if (isGironi && (!values.gironiNumber || +values.gironiNumber < 1)) {
        showToast(
          "gironi-number-error",
          "Inserisci il numero di gironi (almeno 1)",
          "",
          "danger"
        );
        return;
      }
      setBracketLoading(true);
      const isDirect = formula === TournamentFormula.Direct;
      // Finale 3°/4° posto: solo calcio, per eliminazione diretta e gironi (gold).
      const wantThirdPlace = isFootball && (isDirect || isGironi) && thirdPlace;
      // Orari automatici: solo calcio, torneo in giornata con generazione attiva.
      const scheduling =
        isFootball && singleDay && autoTimes && startDateTime
          ? {
              startDateTime,
              matchDurationMinutes: matchDuration,
              // Durata partite fasi finali: solo se il torneo ha gironi + finali.
              ...(isGironi
                ? { knockoutMatchDurationMinutes: knockoutDuration }
                : {}),
              gapMinutes: gapEnabled ? gapMinutes : 0,
              fields: multiField ? Math.max(1, fieldsCount) : 1,
              ...(breakEnabled
                ? {
                    breaks: breaks
                      .filter((b) => b.startTime)
                      .map((b) => ({
                        startTime: b.startTime,
                        durationMinutes: b.durationMinutes,
                      })),
                  }
                : {}),
            }
          : undefined;
      const body = {
        formula,
        gironiNumber: isGironi ? +values.gironiNumber : undefined,
        coppiePerGirone: isGironi ? +values.coppiePerGirone : undefined,
        pairs: values.temporaryPairs,
        testeDiSerie,
        coppieGold:
          isGironi && values.coppieGold ? +values.coppieGold : undefined,
        silver: values.silver,
        // Ripescaggio: n migliori (coppieGold+1)-esime nel Gold (solo senza Silver).
        bestExtra:
          isGironi && !values.silver && values.bestExtra
            ? +values.bestExtra
            : undefined,
        bestExtraMode:
          isGironi && !values.silver && values.bestExtra
            ? values.bestExtraMode || "vsSeeds"
            : undefined,
        // Per il walkover fittizio sui BYE (calcio 6-0, padel 6-0 6-0).
        football: isFootball,
        // Gironi andata e ritorno + criterio della classifica.
        roundTrip: isGironi ? roundTrip : undefined,
        standingsCriteria: isGironi ? standingsCriteria : undefined,
        // Rigori nei pareggi: solo calcio a gironi.
        shootout: isGironi && isFootball && !isBasket ? shootout : undefined,
        shootoutWinPoints:
          isGironi && isFootball && shootout ? shootoutWinPoints : undefined,
        shootoutLosePoints:
          isGironi && isFootball && shootout ? shootoutLosePoints : undefined,
        thirdPlace: wantThirdPlace ? true : undefined,
        scheduling,
      };
      Tournaments.generateTabellone(body).then(
        ({ data }) => { setBracketLoading(false); setBrackets(data); },
        () => {
          setBracketLoading(false);
          showToast("bracket-error", "Errore nella generazione del tabellone", "", "danger");
        }
      );
    },
    [
      isBasket,
      formula,
      testeDiSerie,
      showToast,
      roundTrip,
      standingsCriteria,
      isFootball,
      shootout,
      shootoutWinPoints,
      shootoutLosePoints,
      thirdPlace,
      singleDay,
      autoTimes,
      startDateTime,
      matchDuration,
      knockoutDuration,
      gapEnabled,
      gapMinutes,
      breakEnabled,
      breaks,
      multiField,
      fieldsCount,
    ]
  );

  const handleBracketReset = useCallback(() => setBrackets(undefined), []);

  // Conferma tabellone: QUI vengono persistite TUTTE le modifiche staged
  // (squadre create/eliminate, rose aggiornate) e poi salvato il tabellone.
  const handleBracketSave = useCallback(async () => {
    setSaveBracketLoading(true);
    try {
      let bracketData: any = brackets;
      if (isFootball && tournament) {
        const idMap: Record<string, string> = {};
        const pairs: TemporaryPairs[] =
          tabelloneForm.getFieldValue("temporaryPairs") || [];
        // Creo le squadre locali (con id reale) e salvo tutte le rose.
        for (const team of footballTeams) {
          let realId = team.id;
          const seed = pairs.find((p) => p.id === team.id);
          const name =
            (seed?.player1Name || team.name || "").trim() || "Squadra";
          if (team.id.startsWith("local-")) {
            const { data } = await AdminFootballTeams.createTeam(
              tournament.id!,
              name
            );
            realId = data.team.id;
            idMap[team.id] = realId;
          }
          await AdminFootballTeams.updateMembers(realId, team.members || []);
        }
        // Elimino le squadre rimosse.
        for (const delId of deletedTeamIds) {
          await AdminFootballTeams.deleteTeam(delId);
        }
        // Rimappo gli id locali con quelli reali dentro il tabellone generato.
        if (Object.keys(idMap).length) {
          const remap = (obj: any): any => {
            if (Array.isArray(obj)) return obj.map(remap);
            if (obj && typeof obj === "object") {
              const out: any = {};
              for (const k of Object.keys(obj)) {
                out[k] =
                  k === "id" &&
                  typeof obj[k] === "string" &&
                  idMap[obj[k]]
                    ? idMap[obj[k]]
                    : remap(obj[k]);
              }
              return out;
            }
            return obj;
          };
          bracketData = remap(brackets);
        }
      }
      await Tournaments.saveTabellone(id!, {
        ...bracketData,
        formula,
        // Persisto il criterio della classifica e l'andata/ritorno (gironi).
        // Americano: il criterio (differenza/scontro diretto) è scelto
        // dall'organizzatore; default scontro diretto se non toccato.
        standingsCriteria:
          formula === TournamentFormula.Gironi ||
          formula === TournamentFormula.Americano
            ? standingsCriteria
            : "difference",
        roundTrip: formula === TournamentFormula.Gironi ? roundTrip : false,
        // Rigori nei pareggi dei gironi (solo calcio).
        shootout:
          formula === TournamentFormula.Gironi && isFootball && !isBasket
            ? shootout
            : false,
        shootoutWinPoints,
        shootoutLosePoints,
        // Qualificate al Gold per girone: serve per modificare la fase finale
        // a gironi in corso (pagina tabellone).
        coppieGold:
          formula === TournamentFormula.Gironi
            ? +(tabelloneForm.getFieldValue("coppieGold") || 0) || undefined
            : undefined,
        bestExtra:
          formula === TournamentFormula.Gironi &&
          !tabelloneForm.getFieldValue("silver")
            ? +(tabelloneForm.getFieldValue("bestExtra") || 0) || 0
            : 0,
        bestExtraMode:
          formula === TournamentFormula.Gironi &&
          !tabelloneForm.getFieldValue("silver")
            ? tabelloneForm.getFieldValue("bestExtraMode") || "vsSeeds"
            : undefined,
      });
      setSaveBracketLoading(false);
      showToast(
        "save-bracket-success",
        "Tabellone salvato con successo",
        "",
        "success"
      );
      navigate(`/tornei/${id!}`);
    } catch {
      setSaveBracketLoading(false);
      showToast(
        "save-bracket-error",
        "Errore nel salvataggio del tabellone",
        "",
        "danger"
      );
    }
  }, [
    isBasket,
    brackets,
    formula,
    id,
    navigate,
    showToast,
    isFootball,
    tournament,
    footballTeams,
    deletedTeamIds,
    tabelloneForm,
    standingsCriteria,
    roundTrip,
    shootout,
    shootoutWinPoints,
    shootoutLosePoints,
  ]);

  // ─── bracket parsers ──────────────────────────────────────────────────────

  const formatTeamName = (p: PartialPair | "BYE", football: boolean): string => {
    if (p === "BYE") return "BYE";
    if (football) return p.player1Name || "—";
    return `${p.player1Name}${p.player2Name ? ` / ${p.player2Name}` : ""}`;
  };

  const parseBracketRounds = (rounds: TournamentSchedule["goldBracket"] | TournamentSchedule["rounds"]) =>
    (rounds || []).map((round) => ({
      title: round.name,
      seeds: round.matches.map((m) => ({
        id: `${m.home !== "BYE" ? (m.home as PartialPair).id : "bye"}-${m.away !== "BYE" ? (m.away as PartialPair).id : "bye"}`,
        teams: [
          { name: formatTeamName(m.home, isFootball) },
          { name: formatTeamName(m.away, isFootball) },
        ],
        score: m.score,
        winnerTeam: m.winnerTeam,
        dateTime: m.dateTime,
      })),
    }));

  const parseGoldBracket = useMemo(
    () => parseBracketRounds(brackets?.goldBracket ?? []),
    [brackets, isFootball]
  );
  const parseSilverBracket = useMemo(
    () => parseBracketRounds(brackets?.silverBracket ?? []),
    [brackets, isFootball]
  );
  const parseDirectBracket = useMemo(
    () => parseBracketRounds(brackets?.rounds ?? []),
    [brackets, isFootball]
  );

  const isRiposoMatch = useCallback((fixture: BracketMatch[]) => {
    for (const match of fixture) {
      if (match.home !== "BYE" && match.away !== "BYE") {
        if (match.away.player1Name === "Riposo" || match.away.player2Name === "Riposo")
          return formatTeamName(match.home, isFootball);
        if (match.home.player1Name === "Riposo" || match.home.player2Name === "Riposo")
          return formatTeamName(match.away, isFootball);
      }
    }
    return "";
  }, [isFootball]);

  // ─── render: formula selector ─────────────────────────────────────────────

  const renderFormulaSelector = () => (
    <div style={{ padding: "24px 0 8px" }}>
      {sectionTitle("Seleziona la formula del torneo")}
      <Row className="g-3">
        {[
          {
            value: TournamentFormula.Gironi,
            label: "Gironi",
            desc: isFootball
              ? "Le squadre vengono divise in gironi. I migliori accedono alla fase finale."
              : "Le coppie vengono divise in gironi. Le migliori accedono alla fase ad eliminazione diretta.",
          },
          {
            value: TournamentFormula.Direct,
            label: "Eliminazione Diretta",
            desc: isFootball
              ? "Tabellone ad eliminazione diretta dall'inizio. Chi perde è eliminato."
              : "Tabellone ad eliminazione diretta dall'inizio. Chi perde è eliminato.",
          },
          // Americano: solo padel/tennis (iscrizione individuale, compagni a rotazione).
          ...(!isFootball
            ? [
                {
                  value: TournamentFormula.Americano,
                  label: "Americano",
                  desc: "Iscrizione individuale: i compagni ruotano a ogni giornata, ognuno gioca con tutti. Classifica per singolo giocatore.",
                },
              ]
            : []),
        ].map((opt) => (
          <Col xs={12} md={6} key={opt.value}>
            <div
              onClick={() => handleFormulaChange(opt.value)}
              style={{
                padding: "18px 20px",
                borderRadius: 10,
                border: `2px solid ${formula === opt.value ? accent : "#e8e8e8"}`,
                background: formula === opt.value ? accentSoft : "#fff",
                cursor: "pointer",
                transition: "all 0.15s",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: `2px solid ${formula === opt.value ? accent : "#ccc"}`,
                    background: formula === opt.value ? accent : "transparent",
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontWeight: 700, fontSize: 14 }}>{opt.label}</span>
              </div>
              <p style={{ margin: 0, fontSize: 12, color: "#666", paddingLeft: 28 }}>
                {opt.desc}
              </p>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );

  // ─── render: participant row ───────────────────────────────────────────────

  const renderParticipantRow = (field: any, index: number) => {
    const tempPairs = tabelloneForm.getFieldValue("temporaryPairs") || [];
    const pair = tempPairs[index];
    const isTestaDiSerie = !!testeDiSerie.find((t) => t.id === pair?.id);

    return (
      <div
        key={field.key}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 10px 8px 8px",
          borderRadius: 10,
          border: `1.5px solid ${isTestaDiSerie ? accent : "#ebebeb"}`,
          background: isTestaDiSerie ? accentSoft : "#fff",
          marginBottom: 6,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          transition: "border-color 0.15s",
        }}
      >
        {/* Index badge */}
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            background: isTestaDiSerie ? accent : "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 700,
            color: isTestaDiSerie ? "#fff" : "#999",
            flexShrink: 0,
          }}
        >
          {index + 1}
        </div>

        {isFootball ? (
          <Form.Item name={[field.name, "player1Name"]} style={{ flex: 1, marginBottom: 0 }}>
            <Input
              placeholder="Nome squadra"
              variant="borderless"
              style={{ padding: "2px 6px", fontWeight: 500, fontSize: 13 }}
            />
          </Form.Item>
        ) : (
          <>
            <Form.Item name={[field.name, "player1Name"]} style={{ flex: 1, marginBottom: 0 }}>
              <Input
                placeholder="Giocatore 1"
                variant="borderless"
                style={{ padding: "2px 6px", fontWeight: 500, fontSize: 13 }}
              />
            </Form.Item>
            <div style={{ width: 1, height: 18, background: "#e0e0e0", flexShrink: 0 }} />
            <Form.Item name={[field.name, "player2Name"]} style={{ flex: 1, marginBottom: 0 }}>
              <Input
                placeholder="Giocatore 2"
                variant="borderless"
                style={{ padding: "2px 6px", fontWeight: 500, fontSize: 13 }}
              />
            </Form.Item>
          </>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          <button
            type="button"
            title={isTestaDiSerie ? "Rimuovi testa di serie" : "Segna come testa di serie"}
            onClick={handleAddTestaDiSerie(index)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px 5px",
              color: isTestaDiSerie ? accent : "#ccc",
              lineHeight: 1,
            }}
          >
            <FontAwesomeIcon icon={isTestaDiSerie ? faSolidStar : faStar} size="sm" />
          </button>
          <button
            type="button"
            title={`Rimuovi ${participantLabel}`}
            onClick={handleDelete(field.name)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px 5px",
              color: "#ddd",
              lineHeight: 1,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#dc3545")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#ddd")}
          >
            <FontAwesomeIcon icon={faMinusCircle} size="sm" />
          </button>
        </div>
      </div>
    );
  };

  // ─── render: configuration panel ──────────────────────────────────────────

  const renderConfigPanel = () => {
    if (formula === TournamentFormula.None) return renderFormulaSelector();
    if (brackets) return null;

    return (
      <div>
        {/* Back to formula selector */}
        <button
          type="button"
          onClick={() => { setFormula(TournamentFormula.None); setBrackets(undefined); }}
          style={{
            background: "none",
            border: "none",
            color: "#888",
            fontSize: 12,
            cursor: "pointer",
            padding: 0,
            marginBottom: 16,
          }}
        >
          ← Cambia formula
        </button>

        {formula === TournamentFormula.Gironi && (
          <>
            {sectionTitle("Configurazione gironi")}
            <div
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              {[
                { label: "N° gironi", name: "gironiNumber", placeholder: "Es. 4", min: 1 },
                { label: `${isFootball ? "Squadre" : "Coppie"} / girone`, name: "coppiePerGirone", placeholder: "Es. 4", min: 2 },
                { label: `${isFootball ? "Squadre" : "Coppie"} per il Gold`, name: "coppieGold", placeholder: "Es. 2", min: 1 },
              ].map((field) => (
                <div key={field.name} style={{ flex: "1 1 80px", minWidth: 80 }}>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 4 }}>
                    {field.label}
                  </div>
                  <Form.Item name={field.name} style={{ marginBottom: 0 }}>
                    <Input
                      type="number"
                      min={field.min}
                      placeholder={field.placeholder}
                      style={{ textAlign: "center" }}
                    />
                  </Form.Item>
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>Fase finale</div>
              <Form.Item name="silver" style={{ marginBottom: 0 }}>
                <Radio.Group
                  options={[
                    { label: "Solo Gold", value: false },
                    { label: "Gold + Silver", value: true },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) =>
                  prev.silver !== cur.silver ||
                  prev.coppieGold !== cur.coppieGold ||
                  prev.bestExtra !== cur.bestExtra
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue("silver") ? null : (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 4 }}>
                        Spareggio: migliori{" "}
                        {(+(getFieldValue("coppieGold") || 0) || 0) + 1}ª ripescate
                        nel Gold (0 = nessuna)
                      </div>
                      <Form.Item name="bestExtra" style={{ marginBottom: 0 }}>
                        <Input
                          type="number"
                          min={0}
                          placeholder="Es. 2"
                          style={{ textAlign: "center", maxWidth: 120 }}
                        />
                      </Form.Item>
                      {+(getFieldValue("bestExtra") || 0) > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 4 }}>
                            Metodo di spareggio
                          </div>
                          <Form.Item name="bestExtraMode" style={{ marginBottom: 0 }}>
                            <Radio.Group
                              options={[
                                { label: "Contro le migliori prime", value: "vsSeeds" },
                                { label: "Tra loro", value: "amongThemselves" },
                              ]}
                              optionType="button"
                              buttonStyle="solid"
                            />
                          </Form.Item>
                        </div>
                      )}
                    </div>
                  )
                }
              </Form.Item>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
                Formato gironi
              </div>
              <Radio.Group
                value={roundTrip}
                onChange={(e) => setRoundTrip(e.target.value)}
                options={[
                  { label: "Solo andata", value: false },
                  { label: "Andata e ritorno", value: true },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
                Criterio classifica (a parità di punti)
              </div>
              <Radio.Group
                value={standingsCriteria}
                onChange={(e) => setStandingsCriteria(e.target.value)}
                options={[
                  { label: "Differenza", value: "difference" },
                  { label: "Scontri diretti", value: "headToHead" },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
                {standingsCriteria === "headToHead"
                  ? isFootball
                    ? "Prima gli scontri diretti, poi la differenza reti."
                    : "Prima gli scontri diretti, poi differenza set e game."
                  : isFootball
                  ? "Prima la differenza reti, poi le reti fatte."
                  : "Prima la differenza set, poi la differenza game."}
              </div>
            </div>
            {isFootball && !isBasket && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
                  Pareggi ai calci di rigore
                </div>
                <Radio.Group
                  value={shootout}
                  onChange={(e) => setShootout(e.target.value)}
                  options={[
                    { label: "No", value: false },
                    { label: "Sì", value: true },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
                {shootout ? (
                  <>
                    <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                          Punti a chi vince
                        </div>
                        <InputNumber
                          min={0}
                          max={10}
                          precision={0}
                          value={shootoutWinPoints}
                          onChange={(v) => setShootoutWinPoints(v ?? 0)}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                          Punti a chi perde
                        </div>
                        <InputNumber
                          min={0}
                          max={10}
                          precision={0}
                          value={shootoutLosePoints}
                          onChange={(v) => setShootoutLosePoints(v ?? 0)}
                          style={{ width: "100%" }}
                        />
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
                      Sulle partite di girone finite in parità si potrà inserire
                      il risultato dei rigori: la partita resta un pareggio (reti
                      e differenza invariate), cambiano solo i punti.
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
                    I pareggi valgono 1 punto a testa.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {formula === TournamentFormula.Americano && (
          <>
            {sectionTitle("Configurazione Americano")}
            <div style={{ fontSize: 11, color: "#aaa", marginBottom: 12 }}>
              Ogni giocatore si iscrive singolarmente; i compagni ruotano a ogni
              giornata, così ognuno gioca con tutti. La classifica somma i punti
              vinti da ciascun giocatore.
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
                Criterio classifica (a parità di punti)
              </div>
              <Radio.Group
                value={standingsCriteria}
                onChange={(e) => setStandingsCriteria(e.target.value)}
                options={[
                  { label: "Differenza", value: "difference" },
                  { label: "Scontri diretti", value: "headToHead" },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
                {standingsCriteria === "headToHead"
                  ? "Prima gli scontri diretti fra i pari punti, poi la differenza punti."
                  : "Prima la differenza punti (vinti − subiti)."}
              </div>
            </div>
          </>
        )}

        {isFootball &&
          (formula === TournamentFormula.Direct ||
            formula === TournamentFormula.Gironi) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
                Finale 3°/4° posto
              </div>
              <Radio.Group
                value={thirdPlace}
                onChange={(e) => setThirdPlace(e.target.value)}
                options={[
                  { label: "No", value: false },
                  { label: "Sì", value: true },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
                I due perdenti delle semifinali si affrontano per il 3°/4° posto;
                la partita viene collocata prima della finale.
              </div>
            </div>
          )}

        {isFootball && (
          <>
            {sectionTitle("Orari partite")}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
                Il torneo si svolge tutto in un giorno?
              </div>
              <Radio.Group
                value={singleDay}
                onChange={(e) => setSingleDay(e.target.value)}
                options={[
                  { label: "No", value: false },
                  { label: "Sì", value: true },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </div>

            {singleDay && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
                  Generare automaticamente gli orari delle partite?
                </div>
                <Radio.Group
                  value={autoTimes}
                  onChange={(e) => setAutoTimes(e.target.value)}
                  options={[
                    { label: "No", value: false },
                    { label: "Sì", value: true },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </div>
            )}

            {singleDay && autoTimes && (
              <div
                style={{
                  border: `1px solid ${accentSoft}`,
                  background: accentSoft,
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 16,
                }}
              >
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 4 }}>
                    Inizio prima partita
                  </div>
                  <Input
                    type="datetime-local"
                    value={startDateTime}
                    onChange={(e) => setStartDateTime(e.target.value)}
                  />
                </div>

                <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 4 }}>
                      {formula === TournamentFormula.Gironi
                        ? "Durata partite gironi (min)"
                        : "Durata di ogni partita (min)"}
                    </div>
                    <InputNumber
                      min={1}
                      max={240}
                      precision={0}
                      value={matchDuration}
                      onChange={(v) => setMatchDuration(v ?? 1)}
                      style={{ width: "100%" }}
                    />
                  </div>
                  {formula === TournamentFormula.Gironi && (
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 4 }}>
                        Durata partite fasi finali (min)
                      </div>
                      <InputNumber
                        min={1}
                        max={240}
                        precision={0}
                        value={knockoutDuration}
                        onChange={(v) => setKnockoutDuration(v ?? 1)}
                        style={{ width: "100%" }}
                      />
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
                    Tempo tra una partita e l'altra?
                  </div>
                  <Radio.Group
                    value={gapEnabled}
                    onChange={(e) => setGapEnabled(e.target.value)}
                    options={[
                      { label: "No", value: false },
                      { label: "Sì", value: true },
                    ]}
                    optionType="button"
                    buttonStyle="solid"
                  />
                  {gapEnabled && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                        Minuti tra una partita e l'altra
                      </div>
                      <InputNumber
                        min={0}
                        max={120}
                        precision={0}
                        value={gapMinutes}
                        onChange={(v) => setGapMinutes(v ?? 0)}
                        style={{ width: "100%" }}
                      />
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
                    Partite su più campi in parallelo?
                  </div>
                  <Radio.Group
                    value={multiField}
                    onChange={(e) => setMultiField(e.target.value)}
                    options={[
                      { label: "No", value: false },
                      { label: "Sì", value: true },
                    ]}
                    optionType="button"
                    buttonStyle="solid"
                  />
                  {multiField && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                        Numero di campi
                      </div>
                      <InputNumber
                        min={2}
                        max={20}
                        precision={0}
                        value={fieldsCount}
                        onChange={(v) => setFieldsCount(v ?? 2)}
                        style={{ width: "100%" }}
                      />
                      <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
                        Le partite di ogni turno vengono distribuite sui campi.
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>
                    Inserire pause durante il torneo?
                  </div>
                  <Radio.Group
                    value={breakEnabled}
                    onChange={(e) => setBreakEnabled(e.target.value)}
                    options={[
                      { label: "No", value: false },
                      { label: "Sì", value: true },
                    ]}
                    optionType="button"
                    buttonStyle="solid"
                  />
                  {breakEnabled && (
                    <div style={{ marginTop: 8 }}>
                      {breaks.map((b, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            gap: 12,
                            marginBottom: 8,
                            alignItems: "flex-end",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                              Inizio pausa {breaks.length > 1 ? i + 1 : ""}
                            </div>
                            <Input
                              type="time"
                              value={b.startTime}
                              onChange={(e) =>
                                setBreaks((prev) =>
                                  prev.map((x, j) =>
                                    j === i
                                      ? { ...x, startTime: e.target.value }
                                      : x
                                  )
                                )
                              }
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                              Durata pausa (min)
                            </div>
                            <InputNumber
                              min={1}
                              max={240}
                              precision={0}
                              value={b.durationMinutes}
                              onChange={(v) =>
                                setBreaks((prev) =>
                                  prev.map((x, j) =>
                                    j === i
                                      ? { ...x, durationMinutes: v ?? 1 }
                                      : x
                                  )
                                )
                              }
                              style={{ width: "100%" }}
                            />
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={breaks.length <= 1}
                            onClick={() =>
                              setBreaks((prev) =>
                                prev.filter((_, j) => j !== i)
                              )
                            }
                            style={{ marginBottom: 1 }}
                          >
                            <FontAwesomeIcon icon={faMinusCircle} size="sm" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() =>
                          setBreaks((prev) => [
                            ...prev,
                            { startTime: "", durationMinutes: 30 },
                          ])
                        }
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-2" size="sm" />
                        Aggiungi pausa
                      </Button>
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 11, color: "#aaa", marginTop: 10 }}>
                  Gli orari verranno assegnati a tutte le partite (gironi e fasi
                  finali). Potrai comunque modificarli a mano sul tabellone.
                </div>
              </div>
            )}
          </>
        )}

        {sectionTitle(`Partecipanti (${(temporaryPairs || []).length} ${participantLabelPlural})`)}

        {pairsLoading ? (
          <div className="d-flex justify-content-center py-4">
            <TailSpin height={40} width={40} color={accent} radius="1" />
          </div>
        ) : (
          <>
            <Form.List name="temporaryPairs">
              {(fields) => (
                <div style={{ maxHeight: 400, overflowY: "auto", paddingRight: 4 }}>
                  {fields.map((field, index) => renderParticipantRow(field, index))}
                </div>
              )}
            </Form.List>

            <button
              type="button"
              onClick={handleAddParticipant}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 14px",
                borderRadius: 9,
                border: `1.5px dashed ${accent}`,
                background: accentSoft,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                color: accentDark,
                marginTop: 8,
                width: "100%",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon icon={faPlus} size="sm" />
              Aggiungi {participantLabel}
            </button>
          </>
        )}

        {isFootball && (
          <div style={{ marginTop: 28 }}>
            {sectionTitle("Rose squadre")}
            <FootballRosterEditor
              teams={teamsWithLiveNames}
              favorites={favorites}
              onMembersChange={handleMembersChange}
              onNameChange={handleNameChange}
              onAddTeam={openAddTeam}
              onDeleteTeam={(team) => removeTeam(team.id)}
            />
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
          <Button
            size="sm"
            disabled={(temporaryPairs || []).length === 0 || bracketLoading}
            type="submit"
            style={{
              background: accentGradient,
              border: "none",
              fontWeight: 600,
              padding: "8px 18px",
              borderRadius: 9,
              boxShadow: `0 4px 12px ${accent}44`,
            }}
          >
            {bracketLoading ? (
              <TailSpin color="#fff" width={14} height={14} />
            ) : (
              <>
                <FontAwesomeIcon icon={faArrowRight} className="me-2" />
                Genera Tabellone
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  // ─── render: gironi result ─────────────────────────────────────────────────

  const renderGironi = useCallback(() => {
    if (!brackets?.gironi?.length)
      return <Alert variant="info" className="py-2"><FontAwesomeIcon icon={faInfoCircle} className="me-2" />Nessun girone</Alert>;

    return (
      <>
        {/* Gironi cards */}
        {sectionTitle("Gironi")}
        <Row className="g-3 mb-4">
          {brackets.gironi.map((girone) => (
            <Col xs={12} sm={6} md={4} lg={3} key={girone.name}>
              <Card style={{ ...cardStyle, height: "100%" }}>
                <div style={{ padding: "10px 14px", background: "#fafafa", borderBottom: "1px solid #f0f0f0", fontWeight: 700, fontSize: 13 }}>
                  Girone {girone.name}
                </div>
                <div style={{ padding: "8px 14px" }}>
                  {girone.pairs.length === 0 ? (
                    <small className="text-muted">Nessun partecipante</small>
                  ) : (
                    girone.pairs.map((pair, idx) => (
                      <div key={pair.id} style={{ padding: "6px 0", borderBottom: "1px solid #f5f5f5", fontSize: 13 }}>
                        <span style={{ color: "#888", marginRight: 8, fontSize: 11 }}>{idx + 1}.</span>
                        {isFootball ? pair.player1Name : `${pair.player1Name}${pair.player2Name ? ` / ${pair.player2Name}` : ""}`}
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Calendario */}
        {sectionTitle("Calendario gironi")}
        {brackets.gironi.map((girone, gi) => (
          <div key={gi} style={{ marginBottom: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Girone {girone.name}</div>
            <Row className="g-2">
              {(girone.calendario || []).map((fixture, fi) => (
                <Col xs={12} md={6} lg={4} key={fi}>
                  <Card style={cardStyle}>
                    <div style={{ padding: "8px 12px", background: "#fafafa", borderBottom: "1px solid #f0f0f0", fontSize: 12, fontWeight: 700 }}>
                      {fixture.giornata}ª Giornata
                    </div>
                    <div style={{ padding: "8px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
                      {fixture.matches
                        .filter((m) => (m.home as PartialPair).id !== "BYE" && (m.away as PartialPair).id !== "BYE")
                        .map((match, mi) => (
                          <div key={mi} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            {match.dateTime && (
                              <span style={{ fontSize: 10, fontWeight: 600, color: "#888" }}>
                                {formatPreviewDateTime(match.dateTime)}
                              </span>
                            )}
                            <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                              <span style={{ flex: 1, textAlign: "right" }}>
                                {formatTeamName(match.home, isFootball)}
                              </span>
                              <span style={{ color: "#aaa", fontWeight: 700 }}>vs</span>
                              <span style={{ flex: 1 }}>
                                {formatTeamName(match.away, isFootball)}
                              </span>
                            </div>
                          </div>
                        ))}
                      {isRiposoMatch(fixture.matches) && (
                        <div style={{ fontSize: 11, color: "#888", textAlign: "center", marginTop: 2 }}>
                          Riposa: <strong>{isRiposoMatch(fixture.matches)}</strong>
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ))}

        {/* Fase finale */}
        {sectionTitle(`Tabellone${brackets.silverBracket ? " Gold" : " — Fase Finale"}`)}
        <div style={{ overflowX: "auto", marginBottom: 16 }}>
          <BracketItem rounds={parseGoldBracket} />
        </div>

        {brackets.silverBracket && (
          <>
            {sectionTitle("Tabellone Silver")}
            <div style={{ overflowX: "auto", marginBottom: 16 }}>
              <BracketItem rounds={parseSilverBracket || []} />
            </div>
          </>
        )}
      </>
    );
  }, [brackets, isFootball, isRiposoMatch, parseGoldBracket, parseSilverBracket]);

  // ─── render: direct elimination result ────────────────────────────────────

  const renderTabellone = useCallback(() => {
    if (!brackets?.rounds?.length)
      return <Alert variant="info" className="py-2"><FontAwesomeIcon icon={faInfoCircle} className="me-2" />Nessun tabellone generato</Alert>;

    return (
      <>
        {sectionTitle("Tabellone — Eliminazione Diretta")}
        <div style={{ overflowX: "auto", marginBottom: 16 }}>
          <BracketItem rounds={parseDirectBracket || []} />
        </div>
      </>
    );
  }, [brackets, parseDirectBracket]);

  // ─── render: bracket result area ──────────────────────────────────────────

  const renderBracketResult = () => {
    if (!brackets) return null;

    return (
      <div style={{ marginTop: 8 }}>
        {formula === TournamentFormula.Gironi ||
        formula === TournamentFormula.Americano
          ? renderGironi()
          : renderTabellone()}

        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            paddingTop: 16,
            borderTop: "1px solid #f0f0f0",
            marginTop: 8,
          }}
        >
          <Button variant="outline-secondary" size="sm" onClick={handleBracketReset}>
            Ricalcola
          </Button>
          <Button
            size="sm"
            disabled={saveBracketLoading}
            onClick={handleBracketSave}
            style={{
              background: accentGradient,
              border: "none",
              fontWeight: 600,
              padding: "8px 18px",
              borderRadius: 9,
              boxShadow: `0 4px 12px ${accent}44`,
            }}
          >
            {saveBracketLoading ? (
              <TailSpin color="#fff" width={14} height={14} />
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Salva e pubblica
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  // ─── main render ──────────────────────────────────────────────────────────

  return (
    <Container className="my-3">
      <Helmet>
        <title>{tournament?.name || ""} — Genera Tabelloni</title>
      </Helmet>

      <Breadcrumb className="mb-3">
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/tornei" }}>
          Tornei
        </Breadcrumb.Item>
        {tournament ? (
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/tornei/${tournament.id}` }}>
            {tournament.name}
          </Breadcrumb.Item>
        ) : (
          <Breadcrumb.Item active>
            <TailSpin color={accent} width={14} height={14} />
          </Breadcrumb.Item>
        )}
        <Breadcrumb.Item active>Genera Tabelloni</Breadcrumb.Item>
      </Breadcrumb>

      {/* Page header — banner tematico */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 20,
          padding: "18px 22px",
          background: accentGradient,
          borderRadius: 14,
          boxShadow: `0 8px 22px ${accent}33`,
          color: "#fff",
        }}
      >
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 12,
            background: "rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={sportTheme.icon} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 17,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {tournament?.name ?? (
              <TailSpin color="#fff" width={16} height={16} />
            )}
          </div>
          <div style={{ fontSize: 12.5, opacity: 0.9, marginTop: 2 }}>
            Genera il tabellone
            {isBasket
              ? " del torneo di basket"
              : isFootball
              ? " del torneo di calcio"
              : " del torneo"}
          </div>
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            padding: "5px 12px",
            borderRadius: 999,
            background: "rgba(255,255,255,0.2)",
            whiteSpace: "nowrap",
          }}
        >
          {sportTheme.label}
        </span>
      </div>

      <Form
        form={tabelloneForm}
        onFinish={handleSubmit}
        name="formula"
        initialValues={{
          formula: TournamentFormula.None,
          silver: false,
          bestExtraMode: "vsSeeds",
        }}
      >
        <Form.Item name="formula" hidden><Input /></Form.Item>

        <Row className="g-3">
          {/* Left: config or formula selector */}
          <Col xs={12} lg={brackets ? 12 : 5}>
            <Card style={cardStyle}>
              <div style={{ padding: "12px 20px", borderBottom: "1px solid #f0f0f0", background: "#fafafa", borderLeft: `4px solid ${accent}`, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: accentDark, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, fontSize: 14 }}>
                  {brackets
                    ? "Risultato"
                    : formula === TournamentFormula.None
                    ? "Formula"
                    : `Configurazione — ${formula}`}
                </span>
              </div>
              <div style={{ padding: "16px 20px" }}>
                {brackets ? renderBracketResult() : renderConfigPanel()}
              </div>
            </Card>
          </Col>

          {/* Right: participant summary (only shown before bracket generation) */}
          {!brackets && formula !== TournamentFormula.None && (
            <Col xs={12} lg={7}>
              <Card style={cardStyle}>
                <div style={{ padding: "12px 20px", borderBottom: "1px solid #f0f0f0", background: "#fafafa", borderLeft: `4px solid ${accent}`, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: accentDark, flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, fontSize: 14 }}>
                    Anteprima {participantLabelPlural}
                  </span>
                  {testeDiSerie.length > 0 && (
                    <span style={{ marginLeft: 10, fontSize: 11, color: accent }}>
                      {testeDiSerie.length} testa/e di serie
                    </span>
                  )}
                </div>
                <div style={{ padding: "16px 20px" }}>
                  {pairsLoading ? (
                    <div className="d-flex justify-content-center py-4">
                      <TailSpin height={40} width={40} color={accent} radius="1" />
                    </div>
                  ) : (temporaryPairs || []).length === 0 ? (
                    <Alert variant="info" className="py-2">
                      <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                      Nessun partecipante caricato
                    </Alert>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(temporaryPairs || []).map((p: TemporaryPairs, i: number) => {
                        const isTesta = testeDiSerie.some((t) => t.id === p.id);
                        return (
                          <div
                            key={p.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "8px 12px",
                              borderRadius: 8,
                              background: isTesta ? accentSoft : "#f9f9f9",
                              border: `1px solid ${isTesta ? accent : "#eee"}`,
                              fontSize: 13,
                            }}
                          >
                            <span style={{ color: "#aaa", minWidth: 22, fontSize: 11, fontWeight: 700 }}>{i + 1}.</span>
                            <span style={{ flex: 1 }}>
                              {isFootball
                                ? p.player1Name || <em style={{ color: "#aaa" }}>Nome non inserito</em>
                                : `${p.player1Name || "—"}${p.player2Name ? ` / ${p.player2Name}` : ""}`}
                            </span>
                            {isTesta && (
                              <FontAwesomeIcon icon={faSolidStar} style={{ color: accent, fontSize: 11 }} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          )}
        </Row>
      </Form>

      {/* Modale: nome della nuova squadra (creata solo localmente finche' non
          confermi il tabellone) */}
      <Modal
        title="Nuova squadra"
        open={addTeamOpen}
        onOk={confirmAddTeam}
        onCancel={() => setAddTeamOpen(false)}
        okText="Aggiungi"
        cancelText="Annulla"
        okButtonProps={{ disabled: newTeamName.trim().length < 2 }}
      >
        <Input
          autoFocus
          placeholder="Nome squadra"
          value={newTeamName}
          onChange={(e) => setNewTeamName(e.target.value)}
          onPressEnter={confirmAddTeam}
          maxLength={40}
        />
      </Modal>
    </Container>
  );
}
